// Sidebar · V12 Achromatic Strict · Hotfix header-to-sidebar
// -----------------------------------------------------------------
// Contesto: dashboard loggata storicamente mostrava la nav primaria in orizzontale
// nell'header-v3 (PrimaryNavV3 nel Header.jsx). Richiesta utente: spostare la nav
// nel sidebar sinistra e lasciare l'header con solo logo + icone destra.
//
// Fonte dati: `MENU_GROUPS` da UserWidget/menuGroups.js (SSOT).
// Rendering: ogni gruppo è una sezione collassabile; `workspace` espanso di default.
// Admin groups solo per isAdmin. Launcher link "→ All modules" in fondo (pattern Admin Hub).
import React, { useState, useEffect, useMemo } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
    LogOut, Menu, ChevronLeft, ChevronRight, ChevronDown, X, Boxes, Lock,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/data/v12_user_nav";
import { MENU_GROUPS } from "@/components/header-v3/UserWidget/menuGroups";

// Gruppi espansi per default · workspace + community sono i pulling point utente.
const DEFAULT_EXPANDED = new Set(["workspace", "community"]);
// Admin-only group keys (riconosciuti via `adminOnly: true` o headerKey admin).
const ADMIN_GROUP_KEYS = new Set(["admin", "admin_marketplace", "press", "distribution"]);

function fallbackLabel(groupKey) {
    return groupKey.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function fallbackItemLabel(labelKey) {
    if (!labelKey) return "";
    const last = labelKey.split(".").pop() || "";
    return last.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function SidebarBody({ collapsed, onToggle, onNavigate, mobile }) {
    const { logout, user, hasModule } = useAuth();
    const { t } = useTranslation();
    const location = useLocation();
    const isAdmin = isAdminRole(user?.role);

    // Gruppi visibili per il ruolo corrente.
    const visibleGroups = useMemo(() => {
        return MENU_GROUPS
            .filter((g) => !g.adminOnly || isAdmin)
            .filter((g) => !ADMIN_GROUP_KEYS.has(g.key) || isAdmin);
    }, [isAdmin]);

    // Hotfix single-match active state · "longest-prefix wins" tra TUTTI gli item
    // di TUTTI i gruppi visibili. Garantisce che su /dashboard/legacy solo l'item
    // più specifico (/dashboard/legacy) sia marcato active, non il parent /dashboard.
    const activeItemKey = useMemo(() => {
        const pathname = location.pathname;
        let best = null;
        const consider = (it) => {
            if (!it || !it.to || typeof it.to !== "string" || !it.to.startsWith("/")) return;
            const to = it.to;
            if (pathname === to || pathname.startsWith(to + "/")) {
                if (!best || to.length > best.to.length) best = it;
            }
        };
        visibleGroups.forEach((g) => g.items.forEach(consider));
        // Launcher footer link also eligible.
        consider({ key: "__footer_launcher__", to: "/app/launcher" });
        return best ? best.key : null;
    }, [location.pathname, visibleGroups]);

    const [expanded, setExpanded] = useState(() => {
        const s = new Set();
        visibleGroups.forEach((g) => { if (DEFAULT_EXPANDED.has(g.key)) s.add(g.key); });
        return s;
    });

    const toggleGroup = (key) =>
        setExpanded((prev) => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });

    const renderItem = (item, groupKey) => {
        // adminOnly / module gating.
        if (item.adminOnly && !isAdmin) return null;
        const locked = !isAdmin && item.module && !hasModule && !hasModule(item.module);
        const to = locked ? `/upgrade?module=${item.module}` : item.to;
        const Icon = item.icon;
        const label = item.label || t(item.labelKey, fallbackItemLabel(item.labelKey));
        // Hotfix: active state calcolato esternamente · un solo item attivo per volta.
        const isActive = !locked && activeItemKey === item.key;
        return (
            <NavLink
                key={item.key}
                to={to}
                onClick={onNavigate}
                data-testid={`sidebar-item-${item.key}`}
                aria-current={isActive ? "page" : undefined}
                className={`group relative flex items-center gap-3 rounded-lg pl-8 pr-3 py-1.5 text-sm transition-colors ${
                    isActive
                        ? "bg-[#1A1A1A] text-white border-l-2 border-white"
                        : "text-[#94A3B8] hover:bg-white/5 hover:text-white"
                }`}
            >
                {Icon && <Icon className="h-3.5 w-3.5 shrink-0" />}
                {!collapsed && <span className="truncate">{label}</span>}
                {locked && !collapsed && <Lock className="h-3 w-3 ml-auto opacity-60" />}
            </NavLink>
        );
    };

    return (
        <>
            <div className="flex items-center justify-between px-4 py-5 border-b border-[#1A1A1A]">
                <Link
                    to="/dashboard"
                    onClick={onNavigate}
                    className="flex items-center gap-2 group"
                    data-testid="sidebar-logo"
                >
                    <span className="h-8 w-8 rounded-lg bg-white grid place-items-center shadow-md">
                        <span className="text-[13px] font-bold text-black">G</span>
                    </span>
                    {!collapsed && (
                        <span className="text-sm font-semibold tracking-tight text-white">
                            Global Lead Suite
                        </span>
                    )}
                </Link>
                <button
                    data-testid={mobile ? "sidebar-close-mobile" : "sidebar-toggle"}
                    onClick={onToggle}
                    className="text-neutral-400 hover:text-white transition-colors"
                    aria-label="Toggle sidebar"
                >
                    {mobile ? <X className="h-4 w-4" /> : collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                </button>
            </div>

            <nav className="p-3 pb-20 flex flex-col gap-0.5 overflow-y-auto max-h-[calc(100vh-4rem)]">
                {visibleGroups.map((group) => {
                    const isOpen = expanded.has(group.key) || !!collapsed;
                    const items = group.items.filter((it) => !it.adminOnly || isAdmin);
                    if (items.length === 0) return null;
                    const groupLabel = t(group.headerKey || "", fallbackLabel(group.key));
                    return (
                        <div key={group.key} className="mt-1">
                            {!collapsed && (
                                <button
                                    type="button"
                                    onClick={() => toggleGroup(group.key)}
                                    data-testid={`sidebar-group-${group.key}`}
                                    className="w-full flex items-center justify-between px-3 py-2 text-[10px] uppercase tracking-widest text-neutral-500 hover:text-white transition-colors"
                                >
                                    <span>{groupLabel}</span>
                                    <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? "rotate-0" : "-rotate-90"}`} />
                                </button>
                            )}
                            {isOpen && items.map((item) => renderItem(item, group.key))}
                        </div>
                    );
                })}

                {/* Hotfix FASE B · Launcher link (user hub) */}
                <div className="mt-3 pt-3 border-t border-[#1A1A1A]">
                    <NavLink
                        to="/app/launcher"
                        onClick={onNavigate}
                        data-testid="sidebar-launcher"
                        aria-current={activeItemKey === "__footer_launcher__" ? "page" : undefined}
                        className={`group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                            activeItemKey === "__footer_launcher__"
                                ? "bg-white text-black"
                                : "text-white hover:bg-white/10"
                        }`}
                    >
                        <Boxes className="h-4 w-4 shrink-0" />
                        {!collapsed && <span>→ All modules</span>}
                    </NavLink>
                </div>
            </nav>

            <div className="absolute inset-x-3 bottom-4">
                <button
                    data-testid="sidebar-logout"
                    onClick={logout}
                    className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-neutral-400 hover:text-[#EF4444] hover:bg-[#EF4444]/10 transition-colors"
                >
                    <LogOut className="h-4 w-4 shrink-0" />
                    {!collapsed && <span>Logout</span>}
                </button>
            </div>
        </>
    );
}

export default function Sidebar({ collapsed, onToggle }) {
    // Hotfix header-to-sidebar · F9.5 WAVE 2E deprecation annullata.
    // La sidebar ora renderizza i gruppi di MENU_GROUPS come sezioni collassabili.
    // L'header orizzontale è stato alleggerito (PrimaryNavV3 rimosso in Header.jsx).
    const [mobileOpen, setMobileOpen] = useState(false);

    useEffect(() => {
        const onResize = () => {
            if (window.innerWidth >= 768) setMobileOpen(false);
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    return (
        <>
            {/* Mobile top bar (visible below md) */}
            <div className="md:hidden fixed top-0 left-0 right-0 z-40 flex items-center justify-between border-b border-[#1A1A1A] bg-black/90 backdrop-blur-xl px-4 py-3">
                <Link to="/dashboard" className="flex items-center gap-2">
                    <span className="h-7 w-7 rounded-lg bg-white grid place-items-center">
                        <span className="text-[12px] font-bold text-black">G</span>
                    </span>
                    <span className="text-sm font-semibold tracking-tight text-white">
                        Global Lead Suite
                    </span>
                </Link>
                <button
                    data-testid="sidebar-mobile-open"
                    onClick={() => setMobileOpen(true)}
                    className="h-9 w-9 rounded-lg border border-[#1A1A1A] bg-[#0A0A0A] grid place-items-center text-neutral-400 hover:text-white"
                    aria-label="Open menu"
                >
                    <Menu className="h-4 w-4" />
                </button>
            </div>
            <div className="md:hidden h-[57px] shrink-0" aria-hidden />

            {/* Desktop sidebar */}
            <aside
                data-testid="app-sidebar"
                className={`hidden md:block sticky top-0 h-screen shrink-0 border-r border-[#1A1A1A] bg-[#0A0A0A] overflow-hidden transition-[width] duration-200 ease-out ${
                    collapsed ? "w-[72px]" : "w-[260px]"
                }`}
            >
                <SidebarBody collapsed={collapsed} onToggle={onToggle} mobile={false} />
            </aside>

            {/* Mobile drawer */}
            <AnimatePresence>
                {mobileOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="md:hidden fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
                            onClick={() => setMobileOpen(false)}
                            aria-hidden
                        />
                        <motion.aside
                            data-testid="app-sidebar-mobile"
                            initial={{ x: -300 }}
                            animate={{ x: 0 }}
                            exit={{ x: -300 }}
                            transition={{ duration: 0.22, ease: "easeOut" }}
                            className="md:hidden fixed top-0 left-0 bottom-0 z-50 w-[280px] border-r border-[#1A1A1A] bg-black overflow-y-auto"
                        >
                            <SidebarBody
                                collapsed={false}
                                onToggle={() => setMobileOpen(false)}
                                onNavigate={() => setMobileOpen(false)}
                                mobile={true}
                            />
                        </motion.aside>
                    </>
                )}
            </AnimatePresence>
        </>
    );
}
